// Fehlerbehandlung.
//
// Vorher endeten Fehler in `onclick: async () => …` als unbehandelte
// Promise-Ablehnung: Der Nutzer tippte auf „Speichern", nichts passierte,
// und niemand erfuhr warum. Jede asynchrone Aktion läuft jetzt durch `guard`.
import { ApiError } from './api.js';
import { toast, toastError } from '../ui/toast.js';

export interface GuardOptions {
  /** Text für den Erfolgsfall (Toast). */
  success?: string;
  /** Vorspann für die Fehlermeldung, z.B. „Speichern fehlgeschlagen". */
  failure?: string;
  /** Element, das während der Aktion gesperrt und beschriftet wird. */
  busy?: HTMLButtonElement | null;
}

export function describeError(error: unknown, fallback = 'Etwas ist schiefgelaufen.'): string {
  if (error instanceof ApiError) {
    if (error.isOffline) return 'Keine Verbindung – die Änderung wird nachgeholt.';
    if (error.status === 401) return 'Die Sitzung ist abgelaufen. Bitte neu anmelden.';
    if (error.status === 429) return 'Zu viele Anfragen. Kurz warten und noch einmal versuchen.';
    return error.message || fallback;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/** Führt `fn` aus, meldet Erfolg/Fehler und gibt bei Fehler `null` zurück. */
export async function guard<T>(
  fn: () => Promise<T>,
  { success, failure, busy }: GuardOptions = {},
): Promise<T | null> {
  const originalText = busy?.textContent ?? '';
  if (busy) {
    busy.disabled = true;
    busy.setAttribute('aria-busy', 'true');
  }

  try {
    const result = await fn();
    if (success) toast(success);
    return result;
  } catch (error) {
    const message = describeError(error, failure);
    toastError(failure ? `${failure}: ${message}` : message);
    if (error instanceof ApiError && error.status === 401) {
      window.dispatchEvent(new CustomEvent('speis:unauthorized'));
    }
    return null;
  } finally {
    if (busy) {
      busy.disabled = false;
      busy.removeAttribute('aria-busy');
      if (originalText) busy.textContent = originalText;
    }
  }
}

/** Event-Handler-Fassung von `guard` – direkt an `onclick` übergebbar. */
export function guarded(
  fn: (event: Event) => Promise<unknown>,
  options: GuardOptions = {},
): (event: Event) => void {
  return (event: Event) => {
    const target = event.currentTarget;
    void guard(() => fn(event), {
      ...options,
      busy: options.busy ?? (target instanceof HTMLButtonElement ? target : null),
    });
  };
}

/**
 * Letztes Netz: alles, was trotzdem durchrutscht, wird sichtbar statt
 * still in der Konsole zu verschwinden.
 */
export function installGlobalErrorHandlers(): void {
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as unknown;
    if (reason instanceof ApiError && reason.status === 401) return; // gesondert behandelt
    console.error('[speis] Unbehandelte Ablehnung:', reason);
    toastError(describeError(reason));
  });

  window.addEventListener('error', (event) => {
    // Fehlgeschlagene Bild-/Skript-Ladevorgänge sind kein App-Fehler.
    if (event.target && event.target !== window) return;
    console.error('[speis] Fehler:', event.error);
    toastError(describeError(event.error));
  });
}
